import {
  Box,
  Button,
  Heading,
  Input,
  SimpleGrid,
  Stack,
  Text,
  Flex
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { apiGet, apiUpload } from '../api.js';
import PhotoChoiceModal from '../components/PhotoChoiceModal.jsx';
import AddClientModal from '../components/AddClientModal.jsx';
import { useAuth } from '../context/AuthContext';

const Articles = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [articles, setArticles] = useState([]);
  
  // Create/Edit Form
  const [form, setForm] = useState({
    clientId: '',
    nameOrNo: '',
    date: '',
    weight: '',
    amount: '',
    // goldRate removed
    interestRate: '',
    existingArticleId: '', // For Top-Up Loan
    address: '',
    description: '',
    identityProof: [] // Changed from single type/number to array
  });

  // Client Search/Create State
  const [clientSearch, setClientSearch] = useState('');
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const dropdownRef = useRef(null);
  const [addClientModalOpen, setAddClientModalOpen] = useState(false);

  const [editingId, setEditingId] = useState(null); // ID of article being edited
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Camera/Photo Refs
  const photoUploadRef = useRef(null);
  const photoCameraRef = useRef(null);
  const [photoName, setPhotoName] = useState('');
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);
  const [capturedFile, setCapturedFile] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  const fetchData = async () => {
    try {
      const [clientsRes, articlesRes] = await Promise.all([
        apiGet('/clients'),
        apiGet('/articles')
      ]);
      setClients(clientsRes.clients || []);
      setArticles(articlesRes.articles || []);
    } catch (err) {
      setError(err.message || 'Failed to load data');
    }
  };

  const fetchNameSuggestions = async (search = '') => {
    try {
      const query = new URLSearchParams();
      const term = (search || '').trim();
      if (term) query.set('search', term);
      query.set('limit', '10');
      const response = await apiGet(`/master/article-names?${query.toString()}`);
      setNameSuggestions(response.names || []);
    } catch {
      // Suggestions should not block the form.
    }
  };

  // Load clients and articles on mount
  useEffect(() => {
    fetchData();
    fetchNameSuggestions();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchNameSuggestions(form.nameOrNo);
    }, 200);

    return () => clearTimeout(timer);
  }, [form.nameOrNo]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsClientDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- Camera Logic (Same as before) ---
  useEffect(() => {
    const startCamera = async () => {
      if (!cameraOpen) return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch (err) {
        setCameraError('Camera permission denied or unavailable.');
        setCameraOpen(false);
      }
    };
    startCamera();
    return () => stopCameraStream();
  }, [cameraOpen]);

  const stopCameraStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const closeCamera = () => {
    stopCameraStream();
    setCameraOpen(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.drawImage(video, 0, 0, width, height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setCapturedFile(file);
      setPhotoName(file.name);
      closeCamera();
    }, 'image/jpeg', 0.9);
  };

  const handlePhotoUpload = () => {
    if (photoCameraRef.current) photoCameraRef.current.value = '';
    setPhotoModalOpen(false);
    setCameraError('');
    setCapturedFile(null);
    photoUploadRef.current?.click();
  };

  const handlePhotoCamera = async () => {
    setPhotoModalOpen(false);
    setCameraError('');
    setCapturedFile(null);
    setCameraOpen(true);
  };

  const handlePhotoChange = (input) => {
    const file = input.files?.[0];
    setPhotoName(file ? file.name : '');
    setCapturedFile(null);
  };

  // --- Form Handlers ---

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));

    // Auto-fill logic when selecting an existing article (for Top-Up)
    if (name === 'existingArticleId') {
      if (value) {
        const article = articles.find(a => a._id === value);
        if (article) {
           // Also update client search text if needed (though it should already be set)
           setForm(prev => ({
             ...prev,
             existingArticleId: value,
             clientId: article.clientId?._id || article.client?._id, // Lock client
             nameOrNo: article.nameOrNo, // Lock name
             // Clear amount/date for new loan entry
             amount: '',
             date: new Date().toISOString().split('T')[0],
             // Keep rates or allow override
             // goldRate removed
             interestRate: article.interestRate,
             weight: article.weight,
             // Auto-fill existing details
             address: article.address || '',
             description: article.description || '',
             identityProof: article.identityProof && Array.isArray(article.identityProof) 
               ? article.identityProof 
               : (article.identityProof?.type ? [article.identityProof] : [])
           }));
        }
      } else {
        // Reset if cleared (Top-Up cancelled)
        setForm(prev => ({ 
           ...prev, 
           existingArticleId: '', 
           // We do NOT reset clientId here because user might just want to switch back to "New Article" for SAME client
           // clientId: '', 
           nameOrNo: '',
           address: '',
           description: '',
           identityProof: []
        }));
      }
    }
  };

  // Helper to add/remove ID proof
  const addIdentityProof = () => {
    setForm(prev => ({
      ...prev,
      identityProof: [...prev.identityProof, { type: '', number: '' }]
    }));
  };

  const removeIdentityProof = (index) => {
    setForm(prev => ({
      ...prev,
      identityProof: prev.identityProof.filter((_, i) => i !== index)
    }));
  };

  const updateIdentityProof = (index, field, value) => {
    setForm(prev => {
      const newProofs = [...prev.identityProof];
      newProofs[index] = { ...newProofs[index], [field]: value };
      return { ...prev, identityProof: newProofs };
    });
  };

  // --- Client Dropdown Logic ---
  
  const handleClientSearchChange = (e) => {
    const value = e.target.value;
    setClientSearch(value);
    setIsClientDropdownOpen(true);
    // If user clears search, reset selected client
    if (value === '') {
        setForm(prev => ({ ...prev, clientId: '', existingArticleId: '' }));
    }
  };

  const selectClient = (client) => {
    setForm(prev => ({ ...prev, clientId: client._id, existingArticleId: '' })); // Reset top-up when client changes
    setClientSearch(client.name);
    setIsClientDropdownOpen(false);
  };

  const handleAddClientSuccess = (newClient, isEdit) => {
    if (!isEdit && newClient) {
      setClients(prev => [newClient, ...prev]);
      selectClient(newClient);
    }
    setAddClientModalOpen(false);
  };

  const filteredClients = (clients || []).filter(c => 
    (c && c.name || '').toLowerCase().includes((clientSearch || '').toLowerCase())
  );

  const handleEdit = (article) => {
    setEditingId(article._id);
    const idProof = article.identityProof;
    const idProofArray = Array.isArray(idProof) ? [...idProof] : (idProof && typeof idProof === 'object' ? [{ type: idProof.type || '', number: idProof.number || '' }] : []);
    setForm({
      clientId: article.clientId?._id || article.client?._id || '',
      nameOrNo: article.nameOrNo || '',
      date: article.date ? new Date(article.date).toISOString().split('T')[0] : '',
      weight: article.weight || '',
      amount: article.amount || '',
      interestRate: article.interestRate || '',
      existingArticleId: '',
      address: article.address || '',
      description: article.description || '',
      identityProof: idProofArray,
      identityProofType: article.identityProof?.type || '',
      identityProofNumber: article.identityProof?.number || ''
    });
    
    // Populate Client Search
    const clientName = article.clientId?.name || article.client?.name || '';
    setClientSearch(clientName);
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setForm({
      clientId: '',
      nameOrNo: '',
      date: '',
      weight: '',
      amount: '',
      // goldRate removed
      interestRate: '',
      existingArticleId: '',
      address: '',
      description: '',
      identityProof: [],
      identityProofType: '',
      identityProofNumber: ''
    });
    setClientSearch('');
    setPhotoName('');
    setCapturedFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.clientId) {
        setError('Please select a client first');
        setSaving(false);
        return;
    }

    setSaving(true);
    setError('');
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
         if (key === 'identityProof') {
             formData.append('identityProof', JSON.stringify(value));
         } else if (value !== null && value !== undefined) {
             formData.append(key, value);
         }
      });
      // Ensure clientId is appended if we just auto-created it locally in form object
      if (!formData.get('clientId')) formData.append('clientId', form.clientId);
      
      const photoFile = capturedFile
        || photoCameraRef.current?.files?.[0]
        || photoUploadRef.current?.files?.[0];
      if (photoFile) formData.append('photo', photoFile);

      if (editingId) {
        await apiUpload(`/articles/${editingId}`, formData, 'PUT'); 
      } else {
        await apiUpload('/articles', formData);
      }
      
      handleCancelEdit();
      await fetchData();
    } catch (err) {
      setError(err.message || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={{ base: 4, md: 6 }}>
      <Heading size={{ base: 'md', md: 'lg' }}>Articles</Heading>
      
      {/* ADD / TOP-UP / EDIT FORM */}
      {user?.permissions?.articles?.create && (
        <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={{ base: 3, md: 4 }}>
            <form onSubmit={handleSubmit}>
              <Stack spacing={4}>
                <Flex justify="space-between" align="center">
                    <Text fontWeight="bold" fontSize="lg">
                        {editingId ? 'Edit Article' : 'Add New Article / Top-Up'}
                    </Text>
                    {editingId && (
                        <Button size="xs" onClick={handleCancelEdit}>Cancel Edit</Button>
                    )}
                </Flex>

                {/* Client Selection (Custom Searchable Dropdown) */}
                <Box>
                  <Flex justify="space-between" align="center" mb={2}>
                    <Text fontSize="sm" fontWeight="bold">Client</Text>
                    <Button
                      size="xs"
                      colorScheme="blue"
                      variant="outline"
                      onClick={() => setAddClientModalOpen(true)}
                      isDisabled={!!form.existingArticleId || !!editingId}
                    >
                      + Add New Client
                    </Button>
                  </Flex>
                  <Box position="relative" ref={dropdownRef}>
                    <Input
                      placeholder="Search Client"
                      value={clientSearch}
                      onChange={handleClientSearchChange}
                      onFocus={() => setIsClientDropdownOpen(true)}
                      isDisabled={!!form.existingArticleId || !!editingId}
                    />
                    {isClientDropdownOpen && (
                      <Box
                        position="absolute"
                        top="100%"
                        left={0}
                        right={0}
                        zIndex={10}
                        bg="white"
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        boxShadow="lg"
                        maxH="200px"
                        overflowY="auto"
                        as="ul"
                        listStyleType="none"
                        m={0}
                        p={0}
                      >
                          {filteredClients.map(client => (
                            <Box
                              as="li"
                              key={client._id}
                              px={4}
                              py={2}
                              _hover={{ bg: 'blue.50', cursor: 'pointer' }}
                              onClick={() => selectClient(client)}
                            >
                              {client.name}
                            </Box>
                          ))}
                          {filteredClients.length === 0 && clientSearch && (
                            <Box as="li" px={4} py={2} color="gray.500" fontSize="sm">
                              No clients found.
                            </Box>
                          )}
                      </Box>
                    )}
                  </Box>
                </Box>

                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                  <Box>
                    <Text fontSize="sm" mb={1}>Name/Number</Text>
                    <Input 
                       name="nameOrNo" 
                       value={form.nameOrNo} 
                       onChange={handleChange} 
                       placeholder="Enter name/number" 
                       list="article-name-master-list"
                       isDisabled={!!form.existingArticleId} 
                    />
                    <datalist id="article-name-master-list">
                      {nameSuggestions.map((name) => (
                        <option key={name} value={name} />
                      ))}
                    </datalist>
                  </Box>
                  <Box>
                    <Text fontSize="sm" mb={1}>Date (Loan Date)</Text>
                    <Input type="date" name="date" value={form.date} onChange={handleChange} />
                  </Box>
                  <Box>
                    <Text fontSize="sm" mb={1}>Weight</Text>
                    <Input type="number" name="weight" value={form.weight} onChange={handleChange} />
                  </Box>
                  <Box>
                    <Text fontSize="sm" mb={1}>Amount (Principal)</Text>
                    <Input type="number" name="amount" value={form.amount} onChange={handleChange} />
                  </Box>
                  <Box>
                    <Text fontSize="sm" mb={1}>Interest Rate</Text>
                    <Input type="number" name="interestRate" value={form.interestRate} onChange={handleChange} />
                  </Box>
                  
                  {/* New Fields */}
                  <Box>
                    <Text fontSize="sm" mb={1}>Address</Text>
                    <Input name="address" value={form.address} onChange={handleChange} placeholder="Client Address" />
                  </Box>
                  <Box>
                    <Text fontSize="sm" mb={1}>Description</Text>
                    <Input name="description" value={form.description} onChange={handleChange} placeholder="Item Description" />
                  </Box>
                  
                  {/* ID Proof Array */}
                  <Box gridColumn={{ md: "span 3" }}>
                    <Flex justify="space-between" align="center" mb={2}>
                        <Text fontSize="sm" fontWeight="bold">ID Proofs</Text>
                        <Button size="xs" onClick={addIdentityProof} colorScheme="blue" variant="outline">+ Add ID</Button>
                    </Flex>
                    <Stack spacing={2}>
                        {(form.identityProof || []).map((proof, index) => (
                            <SimpleGrid key={index} columns={{ base: 1, md: 3 }} spacing={2} alignItems="center">
                                <Box>
                                    <select
                                        value={proof.type}
                                        onChange={(e) => updateIdentityProof(index, 'type', e.target.value)}
                                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                                    >
                                        <option value="">Select Type</option>
                                        <option value="Aadhar Card">Aadhar Card</option>
                                        <option value="PAN Card">PAN Card</option>
                                        <option value="Voter ID">Voter ID</option>
                                        <option value="Driving License">Driving License</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </Box>
                                <Box>
                                    <Input 
                                        placeholder="ID Number" 
                                        value={proof.number} 
                                        onChange={(e) => updateIdentityProof(index, 'number', e.target.value)} 
                                    />
                                </Box>
                                <Box>
                                    <Button size="sm" colorScheme="red" variant="ghost" onClick={() => removeIdentityProof(index)}>Remove</Button>
                                </Box>
                            </SimpleGrid>
                        ))}
                        {form.identityProof.length === 0 && <Text fontSize="xs" color="gray.500">No ID proofs added.</Text>}
                    </Stack>
                  </Box>

                  <Box>
                    <Text fontSize="sm" mb={1}>Photo</Text>
                    <Button type="button" variant="outline" onClick={() => setPhotoModalOpen(true)} w={{ base: 'full', md: 'auto' }}>
                      {form.existingArticleId ? 'Add New Photo (Optional)' : 'Upload Photo'}
                    </Button>
                    <Text fontSize="sm" color="gray.500" mt={2}>{photoName || 'No file selected'}</Text>
                    <Input ref={photoUploadRef} type="file" accept="image/*" hidden onChange={() => handlePhotoChange(photoUploadRef.current)} />
                    <Input ref={photoCameraRef} type="file" accept="image/*" capture="environment" hidden onChange={() => handlePhotoChange(photoCameraRef.current)} />
                  </Box>
                  <Box alignSelf={{ base: 'stretch', md: 'flex-end' }}>
                    <Button type="submit" colorScheme="blue" isLoading={saving} w="full" h={{ base: 10, md: 10 }}>
                      {editingId ? 'Update Article' : 'Add Article'}
                    </Button>
                  </Box>
                </SimpleGrid>
              </Stack>
            </form>
        </Box>
      )}

      {error && <Text color="red.500">{error}</Text>}

      <PhotoChoiceModal
        isOpen={photoModalOpen}
        onClose={() => setPhotoModalOpen(false)}
        onUpload={handlePhotoUpload}
        onCamera={handlePhotoCamera}
      />
      
      {/* Camera UI Overlay */}
      {cameraOpen && (
        <Box position="fixed" inset="0" zIndex="1500">
          <Box position="absolute" inset="0" bg="blackAlpha.700" onClick={closeCamera} />
          <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" bg="white" w={{ base: '92%', sm: '420px' }} borderRadius="md" p={4} boxShadow="lg">
            <Text fontSize="lg" fontWeight="semibold" mb={3}>Camera</Text>
            <Box as="video" ref={videoRef} autoPlay playsInline muted w="full" borderRadius="md" bg="black" />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <Stack direction="row" spacing={3} mt={4} justify="flex-end">
              <Button variant="ghost" onClick={closeCamera}>Cancel</Button>
              <Button colorScheme="blue" onClick={capturePhoto}>Take Photo</Button>
            </Stack>
          </Box>
        </Box>
      )}

      <AddClientModal
        isOpen={addClientModalOpen}
        onClose={() => setAddClientModalOpen(false)}
        onSuccess={handleAddClientSuccess}
      />
    </Stack>
  );
};

export default Articles;
